import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { appsAPI, testCasesAPI, publicAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import StatusBadge from '../components/StatusBadge';
import './TestCases.css';

const TestCases = () => {
  const { appId } = useParams();
  const { user, refreshUser } = useAuth();
  const [app, setApp] = useState(null);
  const [testCases, setTestCases] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [showRunResultModal, setShowRunResultModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [logs, setLogs] = useState('');
  const [scriptContent, setScriptContent] = useState('');
  const [runResult, setRunResult] = useState(null);
  const [runningTest, setRunningTest] = useState(false);
  const [formData, setFormData] = useState({ task_description: '', model_id: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [appRes, testRes, modelsRes] = await Promise.all([
        appsAPI.get(appId),
        testCasesAPI.list(appId),
        publicAPI.getModels(),
      ]);
      setApp(appRes.data);
      setTestCases(testRes.data.test_cases);
      setModels(modelsRes.data);

      if (modelsRes.data.length > 0 && !formData.model_id) {
        setFormData((prev) => ({ ...prev, model_id: modelsRes.data[0].id }));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [appId, formData.model_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Poll for in-progress tests
  useEffect(() => {
    const hasInProgress = testCases.some((tc) => tc.status === 'in_progress');
    if (!hasInProgress) return;

    const interval = setInterval(() => {
      fetchData();
      refreshUser(); // Refresh credits after completion
    }, 5000);

    return () => clearInterval(interval);
  }, [testCases, fetchData, refreshUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    const selectedModel = models.find((m) => m.id === parseInt(formData.model_id));
    if (selectedModel && user.credits < selectedModel.credit_cost) {
      setError(`Insufficient credits. You need ${selectedModel.credit_cost} credits.`);
      setSubmitting(false);
      return;
    }

    try {
      await testCasesAPI.create(appId, {
        task_description: formData.task_description,
        model_id: parseInt(formData.model_id),
      });
      setShowModal(false);
      setFormData({ task_description: '', model_id: models[0]?.id || '' });
      fetchData();
      refreshUser();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create test case');
    } finally {
      setSubmitting(false);
    }
  };

  const viewLogs = async (testCase) => {
    setSelectedTest(testCase);
    setLogs('Loading...');
    setShowLogsModal(true);

    try {
      const response = await testCasesAPI.getLogs(testCase.id);
      setLogs(response.data.logs);
    } catch (error) {
      setLogs('Failed to load logs');
    }
  };

  const downloadScript = async (testCase) => {
    try {
      const response = await testCasesAPI.downloadScript(testCase.id);
      const blob = new Blob([response.data], { type: 'application/javascript' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${testCase.tc_number}.spec.js`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to download script');
    }
  };

  const retryTestCase = async (testCase) => {
    try {
      await testCasesAPI.retry(testCase.id);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to retry test case');
    }
  };

  const viewScript = async (testCase) => {
    setSelectedTest(testCase);
    setScriptContent('Loading...');
    setShowScriptModal(true);

    try {
      const response = await testCasesAPI.viewScript(testCase.id);
      setScriptContent(response.data.content);
    } catch (error) {
      setScriptContent('Failed to load script: ' + (error.response?.data?.detail || error.message));
    }
  };

  const runTest = async (testCase) => {
    setSelectedTest(testCase);
    setRunningTest(true);
    setRunResult(null);
    setShowRunResultModal(true);

    try {
      const response = await testCasesAPI.runTest(testCase.id);
      setRunResult(response.data);
    } catch (error) {
      setRunResult({
        success: false,
        error: error.response?.data?.detail || 'Failed to run test'
      });
    } finally {
      setRunningTest(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!app) {
    return <div className="error">Application not found</div>;
  }

  const selectedModel = models.find((m) => m.id === parseInt(formData.model_id));

  return (
    <div className="testcases-page">
      <div className="page-header">
        <div className="breadcrumb">
          <Link to="/applications">Applications</Link>
          <span>/</span>
          <span>{app.name}</span>
        </div>
        <div className="header-content">
          <div>
            <h1>{app.name}</h1>
            <p className="app-url">{app.base_url}</p>
          </div>
          <button onClick={() => setShowModal(true)} className="btn-primary">
            + New Test Case
          </button>
        </div>
      </div>

      {testCases.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🧪</div>
          <h3>No Test Cases Yet</h3>
          <p>Generate your first Playwright test using AI</p>
          <button onClick={() => setShowModal(true)} className="btn-primary">
            Generate Test Case
          </button>
        </div>
      ) : (
        <div className="testcases-list">
          <table>
            <thead>
              <tr>
                <th>TC #</th>
                <th>Task Description</th>
                <th>Status</th>
                <th>Model</th>
                <th>Credits</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {testCases.map((tc) => (
                <tr key={tc.id}>
                  <td className="tc-number">{tc.tc_number}</td>
                  <td className="task-description">{tc.task_description}</td>
                  <td>
                    <StatusBadge status={tc.status} />
                  </td>
                  <td>{tc.model_used || '-'}</td>
                  <td>{tc.credits_used}</td>
                  <td>{new Date(tc.created_at).toLocaleDateString()}</td>
                  <td className="actions-cell">
                    <button onClick={() => viewLogs(tc)} className="action-btn" title="View Logs">
                      📋
                    </button>
                    {tc.status === 'completed' && (
                      <>
                        <button
                          onClick={() => viewScript(tc)}
                          className="action-btn"
                          title="View Script"
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => downloadScript(tc)}
                          className="action-btn"
                          title="Download Script"
                        >
                          ⬇️
                        </button>
                        <button
                          onClick={() => runTest(tc)}
                          className="action-btn run-btn"
                          title="Run Test"
                        >
                          ▶️
                        </button>
                      </>
                    )}
                    {(tc.status === 'failed' || tc.status === 'completed') && (
                      <button
                        onClick={() => retryTestCase(tc)}
                        className="action-btn"
                        title="Retry (no credits charged)"
                      >
                        🔄
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Test Case Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Generate New Test Case</h2>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="task_description">Task Description</label>
                <textarea
                  id="task_description"
                  value={formData.task_description}
                  onChange={(e) => setFormData({ ...formData, task_description: e.target.value })}
                  placeholder="Describe the test scenario in detail, e.g., 'Login with valid credentials and verify the dashboard loads with user's name displayed'"
                  rows={5}
                  minLength={10}
                  required
                />
                <small>Minimum 10 characters. Be specific about what you want to test.</small>
              </div>

              <div className="form-group">
                <label htmlFor="model_id">AI Model</label>
                <select
                  id="model_id"
                  value={formData.model_id}
                  onChange={(e) => setFormData({ ...formData, model_id: e.target.value })}
                  required
                >
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name} ({model.credit_cost} credits)
                    </option>
                  ))}
                </select>
              </div>

              {selectedModel && (
                <div className="credit-info">
                  <p>
                    Cost: <strong>{selectedModel.credit_cost} credits</strong>
                  </p>
                  <p>
                    Your balance: <strong>{user.credits} credits</strong>
                  </p>
                  {user.credits < selectedModel.credit_cost && (
                    <p className="insufficient">Insufficient credits!</p>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting || (selectedModel && user.credits < selectedModel.credit_cost)}
                >
                  {submitting ? 'Generating...' : 'Generate Test'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Logs Modal */}
      {showLogsModal && (
        <div className="modal-overlay" onClick={() => setShowLogsModal(false)}>
          <div className="modal logs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="logs-header">
              <h2>Logs - {selectedTest?.tc_number}</h2>
              <button onClick={() => setShowLogsModal(false)} className="close-btn">
                ×
              </button>
            </div>
            <div className="logs-content">
              <pre>{logs}</pre>
            </div>
          </div>
        </div>
      )}

      {/* Script View Modal */}
      {showScriptModal && (
        <div className="modal-overlay" onClick={() => setShowScriptModal(false)}>
          <div className="modal script-modal" onClick={(e) => e.stopPropagation()}>
            <div className="logs-header">
              <h2>Script - {selectedTest?.tc_number}</h2>
              <button onClick={() => setShowScriptModal(false)} className="close-btn">
                ×
              </button>
            </div>
            <div className="logs-content">
              <pre className="script-content">{scriptContent}</pre>
            </div>
            <div className="modal-actions">
              <button onClick={() => downloadScript(selectedTest)} className="btn-secondary">
                Download
              </button>
              <button onClick={() => { setShowScriptModal(false); runTest(selectedTest); }} className="btn-primary">
                Run Test
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Run Result Modal */}
      {showRunResultModal && (
        <div className="modal-overlay" onClick={() => !runningTest && setShowRunResultModal(false)}>
          <div className="modal run-result-modal" onClick={(e) => e.stopPropagation()}>
            <div className="logs-header">
              <h2>Test Results - {selectedTest?.tc_number}</h2>
              {!runningTest && (
                <button onClick={() => setShowRunResultModal(false)} className="close-btn">
                  ×
                </button>
              )}
            </div>
            <div className="run-result-content">
              {runningTest ? (
                <div className="running-indicator">
                  <div className="spinner"></div>
                  <p>Running test... This may take a few minutes.</p>
                </div>
              ) : runResult ? (
                <>
                  <div className={`result-summary ${runResult.success ? 'success' : 'failed'}`}>
                    <h3>{runResult.success ? '✅ All Tests Passed' : '❌ Tests Failed'}</h3>
                    {runResult.summary && (
                      <div className="summary-stats">
                        <span>Total: {runResult.summary.total}</span>
                        <span className="passed">Passed: {runResult.summary.passed}</span>
                        <span className="failed">Failed: {runResult.summary.failed}</span>
                        <span>Duration: {Math.round(runResult.summary.duration / 1000)}s</span>
                      </div>
                    )}
                  </div>
                  {runResult.tests && runResult.tests.length > 0 && (
                    <div className="test-details">
                      <h4>Test Details:</h4>
                      <ul>
                        {runResult.tests.map((test, idx) => (
                          <li key={idx} className={test.status}>
                            <span className="test-status">{test.status === 'passed' ? '✓' : '✗'}</span>
                            <span className="test-title">{test.title}</span>
                            <span className="test-duration">{test.duration}ms</span>
                            {test.error && <div className="test-error">{test.error}</div>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {runResult.error && (
                    <div className="error-message">{runResult.error}</div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestCases;
